import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PlusCircle, Users, ClipboardList, CheckCircle2 } from "lucide-react";
import { OwlMascot } from "@/components/OwlMascot";

const TeacherDashboard = () => {
  const { user } = useAuth();
  const [experiments, setExperiments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [progress, setProgress] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [experimentId, setExperimentId] = useState("");
  const [emails, setEmails] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [newClassName, setNewClassName] = useState("");
  const [newClassGrade, setNewClassGrade] = useState("");
  const [newClassSubject, setNewClassSubject] = useState("");

  const loadAll = async () => {
    const [e, a, p, s, c] = await Promise.all([
      api.get("/experiments"),
      api.get("/teacher/assignments"),
      api.get("/teacher/progress"),
      api.get("/teacher/students"),
      api.get("/teacher/classes"),
    ]);
    setExperiments(e.data);
    setAssignments(a.data);
    setProgress(p.data);
    setStudents(s.data);
    setClasses(c.data);
  };

  useEffect(() => { loadAll(); }, []);

  const createClass = async () => {
    if (!newClassName.trim()) { toast.error("Give the class a name"); return; }
    try {
      const { data } = await api.post("/teacher/classes", {
        name: newClassName, grade: newClassGrade || null, subject: newClassSubject || null,
      });
      toast.success(`Class created — code: ${data.code}`);
      setNewClassName(""); setNewClassGrade(""); setNewClassSubject("");
      loadAll();
    } catch (err) { toast.error("Could not create class"); }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      await api.post("/teacher/assign", {
        experiment_id: experimentId,
        student_emails: emails.split(/[,\s]+/).filter(Boolean),
        due_date: dueDate || null,
        notes: notes || null,
      });
      toast.success("Assignment created!");
      setOpen(false);
      setExperimentId(""); setEmails(""); setDueDate(""); setNotes("");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to assign");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-700 p-8 text-white relative overflow-hidden">
          <div className="glow-blob bg-cyan-300 w-56 h-56 -bottom-10 -right-10" />
          <div className="relative flex items-center justify-between">
            <div>
              <div className="text-emerald-100 uppercase tracking-widest text-xs font-bold mb-2">Teacher console</div>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl" data-testid="teacher-welcome">Hi Prof. {user?.name?.split(" ")[0]}</h1>
              <p className="mt-3 text-emerald-50 max-w-lg">Assign experiments, monitor progress, and review AI-generated reports.</p>
            </div>
            <div className="hidden lg:block"><OwlMascot size={130} /></div>
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Stat icon={<Users className="h-5 w-5" />} label="Students" value={students.length} testid="stat-students" />
          <Stat icon={<ClipboardList className="h-5 w-5" />} label="Assignments" value={assignments.length} testid="stat-assignments" />
          <Stat icon={<CheckCircle2 className="h-5 w-5" />} label="Submissions" value={progress.reduce((s, a) => s + (a.submitted || 0), 0)} testid="stat-submissions" />
        </div>

        {/* Class codes */}
        <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6">
          <div className="font-outfit font-bold text-xl text-slate-900 mb-4">Class codes</div>
          <div className="grid md:grid-cols-4 gap-2 mb-4">
            <Input placeholder="Class name (e.g. 9A Physics)" value={newClassName} onChange={(e) => setNewClassName(e.target.value)} data-testid="new-class-name-input" />
            <Input placeholder="Grade (IX/X/XI/XII)" value={newClassGrade} onChange={(e) => setNewClassGrade(e.target.value)} data-testid="new-class-grade-input" />
            <Input placeholder="Subject" value={newClassSubject} onChange={(e) => setNewClassSubject(e.target.value)} data-testid="new-class-subject-input" />
            <Button onClick={createClass} className="bg-blue-600 hover:bg-blue-700" data-testid="new-class-create-btn">+ Create class</Button>
          </div>
          {classes.length === 0 ? (
            <div className="text-slate-500 text-sm">No classes yet. Create one — share the code with your students.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {classes.map((c) => (
                <div key={c.class_id} className="p-4 rounded-xl border border-slate-200 hover:border-blue-300 transition-colors" data-testid={`teacher-class-${c.class_id}`}>
                  <div className="font-outfit font-bold text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{c.grade || "—"} · {c.subject || "—"}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="px-2 py-1 rounded bg-blue-50 border border-blue-200 text-blue-700 font-mono font-bold text-sm">{c.code}</span>
                    <span className="text-xs text-slate-500 font-mono">👥 {c.student_count}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-outfit font-bold text-2xl text-slate-900">Assignments</h2>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="teacher-new-assignment-btn">
                <PlusCircle className="mr-2 h-4 w-4" /> New assignment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Assign an experiment</DialogTitle></DialogHeader>
              <form onSubmit={handleAssign} className="space-y-4">
                <div>
                  <Label>Experiment</Label>
                  <Select value={experimentId} onValueChange={setExperimentId}>
                    <SelectTrigger data-testid="assign-exp-select"><SelectValue placeholder="Choose an experiment" /></SelectTrigger>
                    <SelectContent>
                      {experiments.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.subject.toUpperCase()} · {e.grade} · {e.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Student emails (comma or newline separated)</Label>
                  <Textarea value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="a@school.edu, b@school.edu" data-testid="assign-emails-input" />
                </div>
                <div>
                  <Label>Due date (optional)</Label>
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="assign-due-input" />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="assign-notes-input" />
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="assign-submit-btn">Assign</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-4 rounded-2xl bg-white border border-slate-200 overflow-hidden">
          {progress.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No assignments yet. Click &ldquo;New assignment&rdquo; to create one.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3">Experiment</th>
                  <th className="text-left px-6 py-3">Due</th>
                  <th className="text-left px-6 py-3">Progress</th>
                </tr>
              </thead>
              <tbody>
                {progress.map((a) => (
                  <tr key={a.assignment_id} className="border-t border-slate-200" data-testid={`assignment-row-${a.assignment_id}`}>
                    <td className="px-6 py-4 font-semibold text-slate-900">{a.experiment_title}</td>
                    <td className="px-6 py-4 font-mono text-sm text-slate-600">{a.due_date || "—"}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-40 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(a.submitted / Math.max(a.total, 1)) * 100}%` }} />
                        </div>
                        <span className="text-sm font-mono text-slate-600">{a.submitted}/{a.total}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-10">
          <h2 className="font-outfit font-bold text-2xl text-slate-900 mb-4">Students</h2>
          <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-200">
            {students.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No students registered yet.</div>
            ) : students.map((s) => (
              <div key={s.user_id} className="px-6 py-4 flex items-center justify-between" data-testid={`student-row-${s.user_id}`}>
                <div>
                  <div className="font-semibold text-slate-900">{s.name}</div>
                  <div className="text-sm text-slate-500 font-mono">{s.email}</div>
                </div>
                <div className="text-sm text-slate-600 font-mono flex items-center gap-4">
                  <span>⚡ {s.xp} XP</span>
                  <span>📝 {s.reports_count || 0} reports</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

const Stat = ({ icon, label, value, testid }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-200">{icon}</div>
    <div className="mt-3 text-3xl font-outfit font-black text-slate-900">{value}</div>
    <div className="text-sm text-slate-500 uppercase tracking-widest font-bold">{label}</div>
  </div>
);

export default TeacherDashboard;
